import React, { useMemo } from 'react';
import { ToolComponentProps } from './types';
import { ChevronRight, ChevronDown } from 'lucide-react';
import * as Diff from 'diff';
import { truncatePath } from './pathUtils';

interface DiffLine {
  type: 'unchanged' | 'added' | 'removed';
  oldLineNumber?: number;
  newLineNumber?: number;
  content: string;
}

export const StrReplaceTool: React.FC<ToolComponentProps> = ({ 
  input, 
  output, 
  isError,
  isExpanded,
  onToggle 
}) => {
  const path = input?.path || '';
  const displayPath = truncatePath(path);
  const oldStr = input?.old_str || '';
  const newStr = input?.new_str || '';
  const hasOutput = output !== undefined && output !== null;

  // Calculate diff lines
  const diffLines = useMemo(() => {
    if (!oldStr && !newStr) return [];

    const lines: DiffLine[] = [];
    const changes = Diff.diffLines(oldStr, newStr);
    let oldLineNum = 1;
    let newLineNum = 1;

    changes.forEach((part) => {
      const partLines = part.value.split('\n').filter((_, i, arr) =>
        i < arr.length - 1 || arr[i] !== ''
      );

      if (part.added) {
        // Lines added in new version - don't increment oldLineNum
        partLines.forEach(line => {
          lines.push({
            type: 'added',
            newLineNumber: newLineNum++,
            content: line
          });
        });
      } else if (part.removed) {
        // Lines removed from old version - don't increment newLineNum
        partLines.forEach(line => {
          lines.push({
            type: 'removed',
            oldLineNumber: oldLineNum++,
            content: line
          });
        });
      } else {
        // Unchanged lines - increment both
        partLines.forEach(line => {
          lines.push({
            type: 'unchanged',
            oldLineNumber: oldLineNum++,
            newLineNumber: newLineNum++,
            content: line
          });
        });
      }
    });

    // Trim excessive unchanged lines at start and end
    const CONTEXT_LINES = 4;

    // Find first and last changed line indices
    let firstChangedIndex = lines.findIndex(line => line.type !== 'unchanged');
    let lastChangedIndex = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
      if (lines[i].type !== 'unchanged') {
        lastChangedIndex = i;
        break;
      }
    }

    // If no changes, return all lines
    if (firstChangedIndex === -1) return lines;

    // Trim from start: keep only CONTEXT_LINES before first change
    const startTrimIndex = Math.max(0, firstChangedIndex - CONTEXT_LINES);

    // Trim from end: keep only CONTEXT_LINES after last change
    const endTrimIndex = Math.min(lines.length, lastChangedIndex + CONTEXT_LINES + 1);

    return lines.slice(startTrimIndex, endTrimIndex);
  }, [oldStr, newStr]);

  // Calculate statistics
  const stats = useMemo(() => {
    const changes = Diff.diffLines(oldStr, newStr);
    let added = 0;
    let removed = 0;
    
    changes.forEach(part => {
      if (part.added) {
        added += part.count || 0;
      } else if (part.removed) {
        removed += part.count || 0;
      }
    });
    
    return { added, removed };
  }, [oldStr, newStr]);

  return (
    <div>
      <div
        className={`flex items-center gap-1 ${output ? 'cursor-pointer hover:bg-accent/30' : ''} rounded transition-colors`}
        onClick={output ? onToggle : undefined}
        title={path} // Show full path on hover
      >
        <span className="flex-1">
          Edit: {displayPath}
          {!isError && (
            <span className="text-muted-foreground ml-2">
              (<span className="text-green-600">+{stats.added}</span>
              {' '}
              <span className="text-red-600">-{stats.removed}</span>)
            </span>
          )}
        </span>
        <span className="text-muted-foreground inline-block w-3">
          {output && (isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)}
        </span>
      </div>

      {/* Always show diff if not an error */}
      {hasOutput && !isError && diffLines.length > 0 && (
        <div className="mt-2 rounded overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <tbody>
                {diffLines.map((line, index) => {
                  let textColor = '';
                  let prefix = ' ';

                  if (line.type === 'added') {
                    textColor = 'text-green-600';
                    prefix = '+';
                  } else if (line.type === 'removed') {
                    textColor = 'text-red-600';
                    prefix = '-';
                  } else {
                    textColor = 'text-gray-500';
                  }

                  // Determine which line number to show
                  let lineNumber = '';
                  if (line.type === 'unchanged') {
                    lineNumber = line.newLineNumber?.toString() || '';
                  } else if (line.type === 'removed') {
                    lineNumber = line.oldLineNumber?.toString() || '';
                  } else if (line.type === 'added') {
                    lineNumber = line.newLineNumber?.toString() || '';
                  }

                  return (
                    <tr key={index}>
                      <td className="pl-2 pr-1 py-0 text-gray-400 text-right select-none w-8">
                        {lineNumber}
                      </td>
                      <td className={`pl-3 pr-2 py-0 ${textColor}`}>
                        <div className="flex">
                          <span className="select-none inline-block w-4 text-center">{prefix}</span>
                          <pre className="whitespace-pre flex-1">{line.content}</pre>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Show error always */}
      {hasOutput && isError && (
        <div className="mt-2 bg-destructive/10 rounded">
          <div className="text-destructive">
            <pre className="whitespace-pre-wrap">
              {typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Only show tool response when expanded */}
      {isExpanded && output && !isError && (
        <div className="mt-2 text-muted-foreground border-t border-border">
          {typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
        </div>
      )}
    </div>
  );
};